import { useQuery } from '@tanstack/react-query';
import { ActivityIcon, FunnelIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import {
	type MappedAssetSensor,
	mapAssetSensor,
	type SensorStatus,
	type SensorTypeCode,
	sensorStatusLabels,
	sensorTypeSettings,
} from '@/common/asset-sensor';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatDateDistanceFromNow, formatDateTime } from '@/lib/utils';
import { useQueryListAssetSensorsOptions } from '@/queries/assets/sensors/list-sensors';

export type SensorsProps = {
	assetId: string;
	selectedSensor: MappedAssetSensor | null;
	onSelectSensor: (sensor: MappedAssetSensor) => void;
};

export function Sensors({ assetId, selectedSensor, onSelectSensor }: SensorsProps) {
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const sensorIdFromUrl = searchParams.get('sensorId');

	const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

	const { data: sensorsResponse } = useQuery(useQueryListAssetSensorsOptions(assetId as string));
	const [selectedType, setSelectedType] = useState<string>('all');
	const [selectedStatus, setSelectedStatus] = useState<string>('all');
	const sensors = sensorsResponse?.sensors || [];
	const typeLabels = useMemo(() => sensorTypeSettings(t), [t]);
	const statusLabels = useMemo(() => sensorStatusLabels(t), [t]);
	const { mappedSensors, availableTypes, availableStatuses } = useMemo(() => {
		const mappedSensors = sensors.map(mapAssetSensor);
		const availableTypesObj = mappedSensors.reduce(
			(obj, sensor) => {
				obj[sensor.type] = typeLabels[sensor.type].label;
				return obj;
			},
			{} as Record<SensorTypeCode, string>,
		);
		const availableTypes = Object.entries(availableTypesObj).map(([value, label]) => ({
			value,
			label,
		}));

		const availableStatusesObj = mappedSensors.reduce(
			(obj, sensor) => {
				obj[sensor.status] = sensor.statusSettings;
				return obj;
			},
			{} as Record<SensorStatus, MappedAssetSensor['statusSettings']>,
		);
		const availableStatuses = Object.entries(availableStatusesObj).map(([value, statusSettings]) => ({
			value,
			statusSettings,
		}));
		return {
			mappedSensors,
			availableTypes,
			availableStatuses,
		};
	}, [sensors, typeLabels]);

	useEffect(() => {
		if (sensorIdFromUrl && mappedSensors.length > 0) {
			if (selectedSensor?.uniqId && selectedSensor.uniqId === sensorIdFromUrl) {
				return; // Already selected, no action needed
			}

			const foundSensor = mappedSensors.find((s) => s.uniqId === sensorIdFromUrl);

			if (foundSensor) {
				onSelectSensor(foundSensor);

				// Scroll into view after a brief render delay
				setTimeout(() => {
					const el = cardRefs.current[foundSensor.uniqId];
					if (el) {
						el.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}
				}, 100);
			}
		}
	}, [sensorIdFromUrl, mappedSensors, onSelectSensor, selectedSensor]);

	// Apply filters
	const filteredByTypeSensors =
		selectedType === 'all' ? mappedSensors : mappedSensors.filter((sensor) => sensor.type === selectedType);
	const filteredByStatusSensors =
		selectedStatus === 'all'
			? filteredByTypeSensors
			: filteredByTypeSensors.filter((sensor) => sensor.status === selectedStatus);
	const finalMappedSensors = filteredByStatusSensors;

	const handleSensorClick = (sensor: MappedAssetSensor) => {
		onSelectSensor(sensor);
	};

	return (
		<div className="space-y-3">
			<div className="flex items-center gap-2 rounded-lg border bg-card p-2 shadow-sm">
				<FunnelIcon className="ml-2 size-4 text-muted-foreground" />
				<div className="mx-2 h-4 w-px bg-muted-foreground" />
				<Select value={selectedType} onValueChange={(value) => setSelectedType(value)}>
					<SelectTrigger className="bg-input">
						<SelectValue placeholder={t('health.sensors.allTypes')} />
					</SelectTrigger>
					<SelectContent position="popper" className="bg-muted">
						<SelectItem value="all">{t('health.sensors.allTypes')}</SelectItem>
						{availableTypes.map((type) => (
							<SelectItem key={type.value} value={type.value}>
								{type.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<div className="mx-2 h-4 w-px bg-input" />
				<Select value={selectedStatus} onValueChange={(value) => setSelectedStatus(value)}>
					<SelectTrigger className="bg-input">
						<SelectValue placeholder={t('health.sensors.allStatuses')} />
					</SelectTrigger>
					<SelectContent position="popper" className="bg-muted">
						<SelectItem value="all">{t('health.sensors.allStatuses')}</SelectItem>
						{availableStatuses.map((status) => (
							<SelectItem key={status.value} value={status.value}>
								<Badge className={`text-xs ${status.statusSettings.badgeStyle}`}>{statusLabels[status.value]}</Badge>
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
			{finalMappedSensors.map((sensor) => (
				<Card
					key={sensor.uniqId}
					ref={(el) => {
						cardRefs.current[sensor.uniqId] = el;
					}}
					className={cn(
						'cursor-pointer transition-all hover:shadow-md border bg-card py-0',
						selectedSensor?.uniqId === sensor.uniqId &&
							'border-blue-500 bg-blue-50 dark:bg-blue-950 dark:border-blue-800 shadow-md',
					)}
					onClick={() => handleSensorClick(sensor)}
				>
					<CardContent className="p-4">
						<div className="flex items-center justify-between mb-2">
							<h3 className="font-semibold text-sm">
								{sensor.label}{' '}
								<span className="text-muted-foreground font-normal">
									{t('health.sensors.idLabel', { id: sensor.id })}
								</span>
							</h3>
							<Badge className={`text-xs ${sensor.statusSettings.badgeStyle}`}>{statusLabels[sensor.status]}</Badge>
						</div>
						<div className="flex items-center justify-between text-xs">
							<span className="text-muted-foreground">
								{typeLabels[sensor.type].label} • {sensor.dataLoggerName}
							</span>
							<Tooltip>
								<TooltipTrigger asChild>
									<span className="text-zinc-400">
										{t('health.sensors.last', { time: formatDateDistanceFromNow(sensor.lastReading) })}
									</span>
								</TooltipTrigger>
								<TooltipContent>
									<span>{t('health.sensors.exactTime', { time: formatDateTime(sensor.lastReading) })}</span>
								</TooltipContent>
							</Tooltip>
						</div>
					</CardContent>
				</Card>
			))}
			{mappedSensors.length === 0 && (
				<div className="text-center py-12 bg-zinc-100 rounded-lg border border-dashed border-zinc-300">
					<ActivityIcon className="h-8 w-8 mx-auto mb-2 text-zinc-400" />
					<p className="text-muted-foreground">{t('health.sensors.noMatch')}</p>
				</div>
			)}
		</div>
	);
}
